// src/portal-app/operations/ReservationRows.js
//
// The READ path on the Reservations surface — how one reservation and one
// waiting-list entry render, plus their status-contextual actions.
//
// Split out of ReservationsScreen.js in Plan 4 Task 7 (extraction commit),
// then migrated to CondensedRow in the P4 commit. See reservationsShared.js
// for the split boundaries and ReservationsScreen.js for the P4 decisions.
//
// FIELD MAPPING — nothing operational is dropped, only re-sited:
//
//   Reservation row
//     icon       IconGuests (CondensedRow always draws the 32px square; a
//                row with no icon prop wastes that space on nothing)
//     tone       RES_TONE[status]
//     name       guest name — the one thing staff scan a booking list for
//     value      time of day, derived from scheduled_for. CRITICAL: never
//                res.time, which the API does not return (W3 bug 2).
//     secondary  "Party of 4 · Main Hall · Table 5 · 90 min · Birthday" —
//                party size, dining option, table number, duration estimate
//                and notes, in that order, each omitted cleanly when unset.
//                CondensedRow sets title={secondary}, so a visually
//                truncated tail stays reachable on hover/long-press.
//     pill       the status. CondensedRow's pill IS this kit's status-badge
//                slot (there is no node slot for a StatusBadge element), and
//                the pill's LABEL carries the status word — colour is never
//                the sole carrier of meaning (spec §9). The pre-migration
//                StatusBadge element, and with it the shared
//                `data-testid="status-badge"`, is therefore gone from this
//                surface; the row's own `row-pill` replaces it.
//     actions    booked only: Seat / Cancel / No Show, unchanged.
//
//   Waiting-list row
//     name       "#1 · John Smith" — position folded into the name rather
//                than into CondensedRow's icon slot, which is aria-hidden:
//                queue position is meaningful and must reach a screen
//                reader, so it cannot live in a decorative slot. This is the
//                same "{ordinal} · {who}" shape TransactionsScreen uses for
//                "{time} · {staff}".
//     value      elapsed wait — the number that decides who gets seated next.
//     secondary  "Party of 3 · Expected 20m" (expected_wait_minutes kept).
//     pill       waiting / notified, label-carried as above.
import React from 'react';
import { CondensedRow } from '../shell/kit';
import {
    fmtScheduledFor, fmtWaitTime, RES_TONE, WL_TONE,
} from './reservationsShared';

/* ------------------------------------------------------------------ */
/* Inline SVG icon — two guests. Never emoji.                          */
/* ------------------------------------------------------------------ */
const IconGuests = ({ size = 16 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

/** Human-readable label for a snake_case status ('no_show' -> 'no show'). */
function statusLabel(status, t) {
    if (!status) return '';
    return t(status, {}, status.replace(/_/g, ' '));
}

/* ------------------------------------------------------------------ */
/* ReservationRow                                                      */
/* ------------------------------------------------------------------ */
export function ReservationRow({ res, diningOptions, onSeat, onCancel, onNoShow, t }) {
    const diningOption = diningOptions.find((d) => d.id === res.dining_option_id);
    // CRITICAL: derive time from scheduled_for, NOT from res.time (which doesn't exist)
    const displayTime = fmtScheduledFor(res.scheduled_for);
    const tone = RES_TONE[res.status] || 'muted';

    const secondary = [
        t('party_of_n', { count: res.party_size }, 'Party of {count}'),
        diningOption ? diningOption.name : null,
        res.table_number
            ? t('table_n', { number: res.table_number }, 'Table {number}')
            : null,
        res.duration_estimate
            ? t('n_minutes', { count: res.duration_estimate }, '{count} min')
            : null,
        res.notes || null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <div className="res-row-wrap" data-testid="res-card">
            <CondensedRow
                icon={<IconGuests />}
                tone={tone}
                name={res.guest?.name || '—'}
                value={displayTime}
                secondary={secondary || undefined}
                pill={{ label: statusLabel(res.status, t), tone }}
                testId={res.id}
            />

            {/* Status-contextual actions: only booked gets actions */}
            {res.status === 'booked' && (
                <div className="res-actions">
                    <button
                        type="button"
                        className="res-action-btn res-btn-seat"
                        data-testid="res-action-seat"
                        onClick={() => onSeat(res)}
                    >
                        {t('seat', {}, 'Seat')}
                    </button>
                    <button
                        type="button"
                        className="res-action-btn res-btn-cancel"
                        data-testid="res-action-cancel"
                        onClick={() => onCancel(res)}
                    >
                        {t('cancel_reservation', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="res-action-btn res-btn-noshow"
                        data-testid="res-action-noshow"
                        onClick={() => onNoShow(res)}
                    >
                        {t('mark_no_show', {}, 'No Show')}
                    </button>
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* WaitingListRow                                                      */
/* ------------------------------------------------------------------ */
export function WaitingListRow({ entry, onNotify, onSeat, onLeft, onNoShow, t }) {
    const waitDisplay = fmtWaitTime(entry.joined_at, t);
    const tone = WL_TONE[entry.status] || 'muted';

    const canNotify = entry.status === 'waiting';
    const canSeat = entry.status === 'waiting' || entry.status === 'notified';
    const canLeft = entry.status === 'waiting' || entry.status === 'notified';
    const canNoShow = entry.status === 'waiting' || entry.status === 'notified';

    const name = `#${entry.position} · ${entry.guest?.name || '—'}`;
    const secondary = [
        t('party_of_n', { count: entry.party_size }, 'Party of {count}'),
        entry.expected_wait_minutes
            ? t('expected_wait_n', { count: entry.expected_wait_minutes }, 'Expected {count}m')
            : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <div className="res-row-wrap" data-testid="wl-card">
            <CondensedRow
                icon={<IconGuests />}
                tone={tone}
                name={name}
                value={waitDisplay || undefined}
                secondary={secondary || undefined}
                pill={{ label: statusLabel(entry.status, t), tone }}
                testId={`wl-${entry.id}`}
            />

            {(canNotify || canSeat || canLeft || canNoShow) && (
                <div className="wl-actions">
                    {canNotify && (
                        <button
                            type="button"
                            className="wl-action-btn wl-btn-notify"
                            data-testid={`wl-action-notify-${entry.id}`}
                            onClick={() => onNotify(entry)}
                        >
                            {t('notify_guest', {}, 'Notify')}
                        </button>
                    )}
                    {canSeat && (
                        <button
                            type="button"
                            className="wl-action-btn wl-btn-seat"
                            data-testid={`wl-action-seat-${entry.id}`}
                            onClick={() => onSeat(entry)}
                        >
                            {t('seat', {}, 'Seat')}
                        </button>
                    )}
                    {canNoShow && (
                        <button
                            type="button"
                            className="wl-action-btn wl-btn-noshow"
                            data-testid={`wl-action-noshow-${entry.id}`}
                            onClick={() => onNoShow(entry)}
                        >
                            {t('mark_no_show', {}, 'No Show')}
                        </button>
                    )}
                    {canLeft && (
                        <button
                            type="button"
                            className="wl-action-btn wl-btn-left"
                            data-testid={`wl-action-left-${entry.id}`}
                            onClick={() => onLeft(entry)}
                        >
                            {t('mark_left', {}, 'Left')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
